/** Publication facts shared by HTTP and complete sitemap enumeration. */
// Encode only Unicode: do not resolve dot segments, change reserved delimiters,
// repair malformed escapes or otherwise turn an invalid source into an alias.
export const cmsWirePath = value => value.replace(/[^\u0000-\u007f]+/gu, encodeURIComponent);
export const cmsLanguage = value => String(value || "").toLowerCase().replace(/^no(?=-|$)/, "nb");
export function cmsLanguageMatches(left, right) {
  const a = cmsLanguage(left), b = cmsLanguage(right);
  return !!a && !!b && (a === b || !a.includes("-") && b.startsWith(a + "-") ||
    !b.includes("-") && a.startsWith(b + "-"));
}
export function cmsHreflang(language, country) {
  const locale = new Intl.Locale(cmsLanguage(language));
  return locale.region ? locale.baseName : `${locale.baseName}-${country}`;
}

export function cmsCanonicalPath(page, market, routePolicy) {
  const path = "/" + String(page.full_path || "").replace(/^\/+|\/+$/g, "");
  // Owner-approved continuity for the actual published French Belgian page.
  // Its historic unqualified address is not a Dutch translation or the
  // different reviewed restoration identity. Preserve source content/flags.
  if (page.id === 642 && page.locale === "be" && page.language === "fr" &&
      page.slug === "rejoignez-nous" && path === "/be/rejoignez-nous") return path;
  const prefix = `/${page.locale}/`;
  if (!market || !path.startsWith(prefix)) return path;
  const tail = path.slice(prefix.length);
  const [segment, ...rest] = tail.split("/");
  if (cmsLanguageMatches(page.language, market.defaultLanguage)) {
    return cmsLanguageMatches(segment, market.defaultLanguage)
      ? `/${page.locale}${rest.length ? "/" + rest.join("/") : ""}` : path;
  }
  // An explicit same-source canonical is stronger than a generated language
  // prefix. Policies without an actual canonical supply no such evidence.
  // Default-language duplicates above must still collapse in one hop.
  if (routePolicy?.kind === "canonical" && routePolicy.canonical === path) return path;
  return segment === page.language ? path : `${prefix}${page.language}/${tail}`;
}

/** A redundant default-language storage key is an alias only when the actual
 * published same-market, same-slug, same-language primary exists. */
export function cmsPrimarySource(page, inventory, market) {
  if (!market || !cmsLanguageMatches(page.language, market.defaultLanguage)) return page;
  const prefix = `${page.locale}/`;
  const stored = String(page.full_path || "").replace(/^\/+|\/+$/g, "");
  const parts = stored.split("/");
  if (parts[0] !== page.locale || !cmsLanguageMatches(parts[1], market.defaultLanguage)) return page;
  const primaryPath = prefix + parts.slice(2).join("/");
  const matches = inventory.filter(row => row.active !== false &&
    row.locale === page.locale && row.slug === page.slug &&
    cmsLanguageMatches(row.language, page.language) &&
    String(row.full_path || "").replace(/^\/+|\/+$/g, "") === primaryPath);
  if (matches.length > 1) throw Error("Ambiguous CMS primary publication");
  return matches[0] || page;
}

export function cmsTemplateOwnsPath(pathname, { seed, decision } = {}) {
  return !!(seed?.kind === "landing" && seed.landing?.path === pathname && seed.landing.page?.active ||
    decision?.kind === "page" && decision.pathname === pathname &&
    /^landing:[1-9]\d*$/.test(decision.contentIdentity || ""));
}

/** Missing authored title is a publication defect, not a renderer exception.
 * Informational sources additionally need actual copy, never generated prose.
 * Bulk readers transport presence facts, not the authored body itself. */
export function cmsMetadataPublishable(page) {
  const text = value => typeof value === "string" &&
    !!value.replace(/<[^>]*>|&nbsp;|&#160;/gi, " ").trim();
  const hasTitle = page.cms_has_title ?? (Object.hasOwn(page, "page_title")
    ? [page.page_title, page.hero_heading, page.name].some(text) : undefined);
  const hasCopy = page.cms_has_copy ?? (Object.hasOwn(page, "hero_body")
    ? [page.seo_meta_description, page.page_description, page.hero_body].some(text) : undefined);
  const informational = ["info", "static", "reviews", "article", "legal", "help / info"].includes(page.page_type) ||
    page.page_type === "marketing" && page.category === "reviews";
  return hasTitle !== false && (!informational || hasCopy !== false);
}
